import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2,
  CheckCircle2,
  Calendar,
  MapPin,
  Monitor,
  Building2,
  Laptop,
  ExternalLink,
  FileText,
  StickyNote,
  Check,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Users,
  Plus,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * ProfileQuickQuotePopup
 *
 * A specialized quick quote dialog for the profile page that:
 * 1. Works for a single user (no delegate selection needed)
 * 2. Detects existing draft/live quotes for the same requirement in the space
 * 3. Allows 1-click to add user to an existing quote
 */
const ProfileQuickQuotePopup = ({
  isOpen,
  onClose,
  userId,
  userName,
  userAvatar,
  requirementId,
  requirementName,
  projectId,
  onSuccess
}) => {
  const navigate = useNavigate();
  const { userProfile: currentUser, company } = useAuth();

  const [step, setStep] = useState('loading'); // loading, select, form, saving, success
  const [existingQuotes, setExistingQuotes] = useState([]);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [addedToQuoteId, setAddedToQuoteId] = useState(null);
  const [savingAction, setSavingAction] = useState(null); // 'adding' or 'creating'
  const [monthPickerOpen, setMonthPickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [monthPickerDate, setMonthPickerDate] = useState(new Date());

  const [formData, setFormData] = useState({
    deliveryMethods: ['onsite'],
    location: '',
    datePreference: 'asap',
    preferredMonth: '',
    preferredDate: '',
    notes: ''
  });

  // Load existing quotes
  const loadExistingQuotes = useCallback(async () => {
    setStep('loading');
    try {
      // Find existing quotes for the same requirement in the same space
      // that are in draft or quoting status (not completed/cancelled)
      const { data: quotes, error } = await supabase
        .from('training_quotes')
        .select(`
          id,
          course_name,
          status,
          delegates,
          delegate_names,
          date_prefs,
          locations,
          created_date,
          requested_by,
          user_profiles:requested_by (
            first_name,
            last_name
          )
        `)
        .eq('project_id', projectId)
        .eq('requirement_id', requirementId)
        .in('status', ['draft', 'quoting', 'quoted'])
        .order('created_date', { ascending: false });

      if (error) throw error;

      // Filter out quotes that already include this user
      const availableQuotes = (quotes || []).filter(quote => {
        const delegateNames = quote.delegate_names || [];
        // Check if user is already in the quote by name match
        return !delegateNames.some(name =>
          name?.toLowerCase() === userName?.toLowerCase()
        );
      });

      setExistingQuotes(availableQuotes);

      // If there are existing quotes, show selection; otherwise go straight to form
      if (availableQuotes.length > 0) {
        setStep('select');
      } else {
        setStep('form');
      }
    } catch (error) {
      console.error('Error loading existing quotes:', error);
      setStep('form'); // Fall back to form on error
    }
  }, [projectId, requirementId, userName]);

  // Load existing quotes when dialog opens
  useEffect(() => {
    if (isOpen && requirementId && projectId) {
      loadExistingQuotes();
    }
  }, [isOpen, requirementId, projectId, loadExistingQuotes]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
      const currentYear = now.getFullYear();

      setFormData({
        deliveryMethods: ['onsite'],
        location: '',
        datePreference: 'asap',
        preferredMonth: `${currentYear}-${currentMonth}`,
        preferredDate: '',
        notes: ''
      });
      setCreatedQuoteId(null);
      setAddedToQuoteId(null);
      setMonthPickerOpen(false);
      setDatePickerOpen(false);
      setMonthPickerDate(new Date());
    }
  }, [isOpen]);

  // Add user to an existing quote
  const handleAddToExistingQuote = async (quote) => {
    setSavingAction('adding');
    setStep('saving');
    try {
      // Update the quote with the new delegate
      const currentDelegateNames = quote.delegate_names || [];
      const newDelegateNames = [...currentDelegateNames, userName];
      const newDelegateCount = (quote.delegates || 0) + 1;

      const { error: updateError } = await supabase
        .from('training_quotes')
        .update({
          delegate_names: newDelegateNames,
          delegates: newDelegateCount,
          updated_date: new Date().toISOString()
        })
        .eq('id', quote.id);

      if (updateError) throw updateError;

      // Create/update compliance_assignment for this user
      const { error: assignError } = await supabase
        .from('compliance_assignments')
        .upsert({
          project_id: projectId,
          requirement_id: requirementId,
          team_member_id: userId,
          assigned: true,
          status: 'quoting',
          quote_id: quote.id
        }, {
          onConflict: 'project_id,requirement_id,team_member_id'
        });

      if (assignError) throw assignError;

      // Update user_qualifications
      const { data: existingQual } = await supabase
        .from('user_qualifications')
        .select('id')
        .eq('user_id', userId)
        .eq('requirement_id', requirementId)
        .maybeSingle();

      if (existingQual) {
        await supabase
          .from('user_qualifications')
          .update({
            renewal_status: 'quoting',
            updated_date: new Date().toISOString()
          })
          .eq('id', existingQual.id);
      } else {
        await supabase
          .from('user_qualifications')
          .insert({
            user_id: userId,
            requirement_id: requirementId,
            status: null,
            renewal_status: 'quoting',
            created_date: new Date().toISOString(),
            updated_date: new Date().toISOString()
          });
      }

      setAddedToQuoteId(quote.id);
      setStep('success');
      toast.success(`Added ${userName} to existing quote`);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding to quote:', error);
      toast.error('Failed to add to quote');
      setStep('select');
    }
  };

  // Create a new quote
  const handleCreateNewQuote = async () => {
    setSavingAction('creating');
    setStep('saving');
    try {
      // Format date preferences
      let formattedDatePrefs = '';
      if (formData.datePreference === 'specific' && formData.preferredDate) {
        formattedDatePrefs = new Date(formData.preferredDate).toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      } else if (formData.preferredMonth) {
        const [year, month] = formData.preferredMonth.split('-');
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
        formattedDatePrefs = `${monthNames[parseInt(month) - 1]} ${year}`;
      }

      // Fetch global (BOHSA) trainers to pre-select as providers
      const { data: globalTrainerData } = await supabase
        .from('trainers')
        .select('id, trainer_name, booking_email')
        .is('company_id', null)
        .order('display_order', { nullsFirst: false })
        .order('trainer_name');

      const defaultTrainers = (globalTrainerData || []).map(t => ({
        id: t.id,
        name: t.trainer_name,
        email: t.booking_email,
        type: 'global',
        wasNotified: false
      }));

      // Build webhook payload
      const webhookPayload = {
        requested_date: new Date().toISOString(),
        course_name: requirementName,
        delegates: 1,
        date_prefs: formattedDatePrefs,
        locations: formData.location || 'Not specified',
        additional_details: `<p><strong>Delegates:</strong> ${userName}</p>
                      <p><strong>Requirements:</strong> ${requirementName}</p>
                      ${formData.notes ? `<p><strong>Additional notes:</strong></p><p>${formData.notes}</p>` : ''}`
      };

      // Create the quote
      const quoteInsertData = {
        project_id: projectId,
        company_id: company?.id,
        requested_by: currentUser?.id,
        requested_date: new Date().toISOString(),
        course_name: requirementName,
        status: 'draft',
        delegates: 1,
        delegate_names: [userName],
        date_prefs: formattedDatePrefs,
        locations: formData.location,
        further_info: formData.notes,
        webhook_payload: webhookPayload,
        delivery_methods: formData.deliveryMethods || ['onsite'],
        requirement_id: requirementId,
        selected_trainers: defaultTrainers
      };

      const { data: quoteData, error: quoteError } = await supabase
        .from('training_quotes')
        .insert(quoteInsertData)
        .select()
        .single();

      if (quoteError) throw quoteError;

      const newQuoteId = quoteData?.id;

      // Create/update compliance_assignment
      const { error: assignError } = await supabase
        .from('compliance_assignments')
        .upsert({
          project_id: projectId,
          requirement_id: requirementId,
          team_member_id: userId,
          assigned: true,
          status: 'quoting',
          quote_id: newQuoteId
        }, {
          onConflict: 'project_id,requirement_id,team_member_id'
        });

      if (assignError) throw assignError;

      // Update user_qualifications
      const { data: existingQual } = await supabase
        .from('user_qualifications')
        .select('id')
        .eq('user_id', userId)
        .eq('requirement_id', requirementId)
        .maybeSingle();

      if (existingQual) {
        await supabase
          .from('user_qualifications')
          .update({
            renewal_status: 'quoting',
            updated_date: new Date().toISOString()
          })
          .eq('id', existingQual.id);
      } else {
        await supabase
          .from('user_qualifications')
          .insert({
            user_id: userId,
            requirement_id: requirementId,
            status: null,
            renewal_status: 'quoting',
            created_date: new Date().toISOString(),
            updated_date: new Date().toISOString()
          });
      }

      setCreatedQuoteId(newQuoteId);
      setStep('success');
      toast.success('Quote created successfully');

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating quote:', error);
      toast.error('Failed to create quote');
      setStep('form');
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state after dialog closes
    setTimeout(() => {
      setStep('loading');
      setExistingQuotes([]);
      setCreatedQuoteId(null);
      setAddedToQuoteId(null);
      setSavingAction(null);
    }, 200);
  };

  const handleViewQuote = () => {
    const quoteId = createdQuoteId || addedToQuoteId;
    handleClose();
    if (quoteId && projectId) {
      navigate(`/space/${projectId}/bookings/${quoteId}`);
    }
  };

  const deliveryOptions = [
    { value: 'onsite', label: 'Onsite', icon: MapPin },
    { value: 'classroom', label: 'Classroom', icon: Building2 },
    { value: 'remote', label: 'Remote', icon: Monitor },
    { value: 'elearning', label: 'E-Learning', icon: Laptop }
  ];

  const dateOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: 'month', label: 'Month' },
    { value: 'specific', label: 'Date' }
  ];

  const toggleDeliveryMethod = (value) => {
    setFormData(prev => {
      const current = prev.deliveryMethods || [];
      const isSelected = current.includes(value);
      let newMethods = isSelected
        ? current.filter(m => m !== value)
        : [...current, value];
      if (newMethods.length === 0) newMethods = [value];

      const hasPhysicalDelivery = newMethods.includes('onsite') || newMethods.includes('classroom');
      const location = hasPhysicalDelivery ? prev.location : '';

      return { ...prev, deliveryMethods: newMethods, location };
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatQuoteDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="sm:max-w-[520px] p-0 gap-0 overflow-hidden"
        showCloseButton={step !== 'saving'}
      >
        {/* Loading state */}
        {step === 'loading' && (
          <div className="flex flex-col items-center text-center px-6 py-12">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            <p className="mt-3 text-sm text-muted-foreground">Checking for existing quotes...</p>
          </div>
        )}

        {/* Selection state - existing quotes available */}
        {step === 'select' && (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle>Quick Quote</DialogTitle>
                  <DialogDescription className="truncate">
                    {requirementName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="px-6 py-5 space-y-4">
              {/* Existing quotes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  Add to existing quote
                </Label>
                <p className="text-xs text-muted-foreground">
                  These quotes are already in progress for this requirement
                </p>

                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-2">
                    {existingQuotes.map((quote) => (
                      <button
                        key={quote.id}
                        onClick={() => handleAddToExistingQuote(quote)}
                        className="w-full p-3 border rounded-lg text-left hover:bg-accent transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px]">
                                {quote.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {formatQuoteDate(quote.created_date)}
                              </span>
                            </div>
                            <p className="text-sm font-medium mt-1 truncate">
                              {quote.course_name}
                            </p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                              <Users className="w-3 h-3" />
                              <span>{quote.delegates || 0} delegate{(quote.delegates || 0) !== 1 ? 's' : ''}</span>
                              {quote.date_prefs && (
                                <>
                                  <span className="text-gray-300">•</span>
                                  <Clock className="w-3 h-3" />
                                  <span>{quote.date_prefs}</span>
                                </>
                              )}
                            </div>
                            {quote.delegate_names?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1 truncate">
                                {quote.delegate_names.slice(0, 3).join(', ')}
                                {quote.delegate_names.length > 3 && ` +${quote.delegate_names.length - 3} more`}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <UserPlus className="w-4 h-4 text-primary" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <Separator />

              {/* Create new option */}
              <div className="flex justify-center">
                <Button
                  onClick={() => setStep('form')}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create new quote
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Form state */}
        {step === 'form' && (
          <>
            <DialogHeader className="px-6 pt-6 pb-4 border-b">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle>New Quote</DialogTitle>
                  <DialogDescription className="truncate">
                    {requirementName}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* User being quoted */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={userAvatar} />
                  <AvatarFallback className="text-xs">{getInitials(userName)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{userName}</p>
                  <p className="text-xs text-muted-foreground">Delegate</p>
                </div>
              </div>

              {/* Delivery Methods */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Delivery Method</Label>
                <div className="grid grid-cols-4 gap-2">
                  {deliveryOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = formData.deliveryMethods?.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleDeliveryMethod(option.value)}
                        className={cn(
                          'relative flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors',
                          isSelected
                            ? 'border-primary bg-accent'
                            : 'border-border hover:bg-muted/50'
                        )}
                      >
                        {isSelected && (
                          <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                          </div>
                        )}
                        <div className={cn(
                          'w-8 h-8 rounded-md flex items-center justify-center',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground'
                        )}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className={cn(
                          'text-xs font-medium',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Location & Date Row */}
              <div className={cn(
                "grid gap-4",
                (formData.deliveryMethods?.includes('onsite') || formData.deliveryMethods?.includes('classroom'))
                  ? "grid-cols-2"
                  : "grid-cols-1"
              )}>
                {/* Location */}
                {(formData.deliveryMethods?.includes('onsite') || formData.deliveryMethods?.includes('classroom')) && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                      Location
                    </Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., London Office"
                    />
                  </div>
                )}

                {/* Date Preference */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                    When
                  </Label>
                  <div className="flex gap-1 p-1 bg-muted rounded-md">
                    {dateOptions.map((option) => {
                      const isSelected = formData.datePreference === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, datePreference: option.value }))}
                          className={cn(
                            'flex-1 py-1.5 px-2 rounded-sm text-xs font-medium transition-colors',
                            isSelected
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Conditional date inputs */}
              {formData.datePreference === 'month' && (
                <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.preferredMonth && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.preferredMonth
                        ? format(new Date(formData.preferredMonth + '-01'), 'MMMM yyyy')
                        : 'Select month'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <div className="flex items-center justify-between mb-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setMonthPickerDate(new Date(monthPickerDate.getFullYear() - 1, monthPickerDate.getMonth()))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium">{monthPickerDate.getFullYear()}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setMonthPickerDate(new Date(monthPickerDate.getFullYear() + 1, monthPickerDate.getMonth()))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 12 }, (_, i) => {
                        const monthDate = new Date(monthPickerDate.getFullYear(), i, 1);
                        const monthValue = format(monthDate, 'yyyy-MM');
                        const isSelected = formData.preferredMonth === monthValue;
                        return (
                          <Button
                            key={i}
                            variant={isSelected ? "default" : "ghost"}
                            size="sm"
                            className="h-8"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, preferredMonth: monthValue }));
                              setMonthPickerOpen(false);
                            }}
                          >
                            {format(monthDate, 'MMM')}
                          </Button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {formData.datePreference === 'specific' && (
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.preferredDate && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.preferredDate
                        ? format(new Date(formData.preferredDate), 'PPP')
                        : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.preferredDate ? new Date(formData.preferredDate) : undefined}
                      onSelect={(date) => {
                        setFormData(prev => ({
                          ...prev,
                          preferredDate: date ? format(date, 'yyyy-MM-dd') : ''
                        }));
                        setDatePickerOpen(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <StickyNote className="w-3.5 h-3.5 text-muted-foreground" />
                  Notes
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special requirements..."
                  rows={2}
                  className="resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t">
              <div>
                {existingQuotes.length > 0 && (
                  <Button variant="ghost" onClick={() => setStep('select')}>
                    Back
                  </Button>
                )}
              </div>
              <Button onClick={handleCreateNewQuote}>
                Create Quote
              </Button>
            </div>
          </>
        )}

        {/* Saving state */}
        {step === 'saving' && (
          <div className="flex flex-col items-center text-center px-6 py-12">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            <p className="mt-3 font-medium">
              {savingAction === 'adding' ? 'Adding to quote...' : 'Creating quote...'}
            </p>
          </div>
        )}

        {/* Success state */}
        {step === 'success' && (
          <div className="flex flex-col items-center text-center px-6 py-12">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
            <p className="mt-3 font-medium">
              {addedToQuoteId ? 'Added to Quote' : 'Quote Created'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {addedToQuoteId
                ? `${userName} has been added to the existing quote`
                : `Your quote for ${requirementName} is ready`
              }
            </p>
            <div className="flex gap-2 mt-5">
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button onClick={handleViewQuote} className="gap-2">
                <ExternalLink className="w-4 h-4" />
                View Quote
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileQuickQuotePopup;
